/**
 * <section> brand-stores-card behaviour: quick-view flip panels, variant
 * selection, auto accent derived from the brand logo, and hover tilt.
 * Externalized from the section's former inline script; initializes every
 * [data-ix-bsc] instance and re-initializes on theme-editor section loads.
 */

function formatMoney(cents, fmt) {
  var fallbackFmt = '${' + '{amount}}';
  try {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, fmt || fallbackFmt);
    }
  } catch (err) {}
  var amt = (cents / 100).toFixed(2);
  return (fmt || '$' + amt).replace(/\{\{\s*amount(_no_decimals|_with_comma_separator)?\s*\}\}/g, amt);
}

function getPanelData(panel) {
  var el = panel.querySelector('.ix-bsc__qv-data');
  if (!el) return { variants: [], money_format: null };
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    return { variants: [], money_format: null };
  }
}

function currentSelections(panel) {
  return Array.prototype.map.call(panel.querySelectorAll('.ix-bsc__qv-option'), function (fs) {
    var chk = fs.querySelector('input[type="radio"]:checked');
    return chk ? chk.value : null;
  });
}

function matchVariant(variants, sel) {
  return variants.find(function (v) {
    if (!v.options || v.options.length !== sel.length) return false;
    return sel.every(function (val, i) {
      return v.options[i] === val;
    });
  });
}

function updatePanelState(panel) {
  var data = getPanelData(panel);
  var sel = currentSelections(panel);
  var v = matchVariant(data.variants, sel);
  var add = panel.querySelector('.ix-bsc__qv-add');
  var submit = panel.querySelector('.ix-bsc__qv-submit');
  var priceNow = panel.querySelector('.ix-bsc__qv-price-now');
  var priceCmp = panel.querySelector('.ix-bsc__qv-compare');
  var img = panel.querySelector('.ix-bsc__qv-img');

  Array.prototype.forEach.call(panel.querySelectorAll('.ix-bsc__qv-option'), function (fs, i) {
    var lbl = fs.querySelector('.ix-bsc__qv-option-selected');
    if (lbl) lbl.textContent = sel[i] ? ': ' + sel[i] : '';
  });

  var submitLabel = submit && (submit.querySelector('.add-to-cart-text') || submit);
  if (!v) {
    if (submit) { submit.disabled = true; submitLabel.textContent = submit.dataset.labelSoldout; }
    return;
  }
  if (add) add.dataset.variant = v.id;
  if (priceNow) priceNow.textContent = formatMoney(v.price, data.money_format);
  if (priceCmp) {
    if (v.compare_at_price && v.compare_at_price > v.price) {
      priceCmp.textContent = formatMoney(v.compare_at_price, data.money_format);
      priceCmp.hidden = false;
    } else {
      priceCmp.hidden = true;
    }
  }
  if (img) {
    var nextSrc = v.featured_image || img.getAttribute('data-default-image') || data.default_image;
    if (nextSrc && img.getAttribute('src') !== nextSrc) {
      img.style.opacity = '0';
      var tmp = new Image();
      tmp.onload = function () { img.src = nextSrc; img.style.opacity = '1'; };
      tmp.onerror = function () { img.style.opacity = '1'; };
      tmp.src = nextSrc;
    }
  }
  if (submit) {
    submit.disabled = !v.available;
    submitLabel.textContent = v.available ? submit.dataset.labelAdd : submit.dataset.labelSoldout;
  }
}

function openQuickView(card, pid) {
  var any = false;
  Array.prototype.forEach.call(card.querySelectorAll('.ix-bsc__qv'), function (p) {
    var active = String(p.getAttribute('data-pid')) === String(pid);
    p.setAttribute('data-active', active ? '1' : '0');
    if (active) any = true;
  });
  if (!any) return false;
  card.classList.add('is-flipped');
  var back = card.querySelector('.ix-bsc__face--back');
  if (back) back.setAttribute('aria-hidden', 'false');
  return true;
}

function closeQuickView(card) {
  card.classList.remove('is-flipped');
  var back = card.querySelector('.ix-bsc__face--back');
  if (back) back.setAttribute('aria-hidden', 'true');
}

// Auto accent: pastelize the brand logo's dominant color and use it as the
// card's --brand-accent. Sampling is logo-specific (skips transparent and
// white pixels), so it stays here rather than the generic ix-accent sampler.
// The saved accent_color paints first and remains the fallback.
function deriveAccent(card) {
  var logo = card.querySelector('.ix-bsc__logo img');
  if (!logo) return;

  function derive() {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        var S = 24;
        var cv = document.createElement('canvas');
        cv.width = S;
        cv.height = S;
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(img, 0, 0, S, S);
        var d = cx.getImageData(0, 0, S, S).data;
        var r = 0, g = 0, b = 0, n = 0;
        for (var i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 200) continue; // transparent
          var R = d[i], G = d[i + 1], B = d[i + 2];
          if (R > 240 && G > 240 && B > 240) continue; // white bg
          r += R; g += G; b += B; n++;
        }
        if (n < 8) return; // logo is essentially white; keep fallback
        r /= n; g /= n; b /= n;
        // lighten toward a uniform pastel (target luminance ~214)
        var L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        var s = Math.min(0.3, 41 / Math.max(1, 255 - L));
        var mix = function (c) { return Math.round(255 - (255 - c) * s); };
        card.style.setProperty('--brand-accent', 'rgb(' + mix(r) + ' ' + mix(g) + ' ' + mix(b) + ')');
      } catch (err) {
        /* tainted canvas or decode issue: keep the fallback accent */
      }
    };
    img.src = logo.currentSrc || logo.src;
  }

  if (logo.complete) derive();
  else logo.addEventListener('load', derive, { once: true });
}

function initRoot(root) {
  if (!root || root.dataset.ixQvInit === '1') return;
  root.dataset.ixQvInit = '1';

  var tiltable = window.matchMedia('(hover: hover) and (prefers-reduced-motion: no-preference)').matches;

  Array.prototype.forEach.call(root.querySelectorAll('.ix-bsc__card'), function (card) {
    Array.prototype.forEach.call(card.querySelectorAll('button[data-qv-open]'), function (btn) {
      btn.addEventListener('click', function () {
        openQuickView(card, btn.getAttribute('data-qv-open'));
      });
    });

    Array.prototype.forEach.call(card.querySelectorAll('.ix-bsc__qv-close'), function (btn) {
      btn.addEventListener('click', function () {
        closeQuickView(card);
      });
    });

    Array.prototype.forEach.call(card.querySelectorAll('.ix-bsc__qv'), function (panel) {
      Array.prototype.forEach.call(panel.querySelectorAll('.ix-bsc__qv-option input[type="radio"]'), function (inp) {
        inp.addEventListener('change', function () {
          updatePanelState(panel);
        });
      });
      updatePanelState(panel);

      // ix-quick-add performs the AJAX add on click; flip back afterwards
      var submit = panel.querySelector('.ix-bsc__qv-submit');
      if (submit) {
        submit.addEventListener('click', function () {
          if (submit.disabled) return;
          setTimeout(function () { closeQuickView(card); }, 350);
        });
      }
    });

    if (card.getAttribute('data-auto-accent') !== '0') deriveAccent(card);

    // 3D tilt toward the cursor, rAF-throttled so pointermove can't thrash layout
    if (tiltable) {
      var tiltFrame = null;
      card.addEventListener('pointermove', function (e) {
        if (tiltFrame) return;
        tiltFrame = requestAnimationFrame(function () {
          tiltFrame = null;
          var r = card.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5;
          var y = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform = 'rotateY(' + (x * 6).toFixed(2) + 'deg) rotateX(' + (y * -6).toFixed(2) + 'deg)';
        });
      });
      card.addEventListener('pointerleave', function () {
        if (tiltFrame) { cancelAnimationFrame(tiltFrame); tiltFrame = null; }
        card.style.transform = '';
      });
    }
  });
}

function initAll(scope) {
  Array.prototype.forEach.call((scope || document).querySelectorAll('[data-ix-bsc]'), initRoot);
}

initAll(document);
document.addEventListener('shopify:section:load', function (e) {
  initAll(e.target);
});
